import { Injectable, InjectionToken, Inject } from '@angular/core';
import { addPendo, PendoConfig } from '@ecoonline/pendo-integration';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';
import { Tenant } from '@app/Models/tenant';
import { environment } from '../../environments/environment';
import { TenantService } from './tenant.service';

/**
 * Injection token wrapping `addPendo` from `@ecoonline/pendo-integration`.
 * ES module exports are non-configurable, so spyOn cannot intercept addPendo directly.
 * Tests inject a spy via { provide: ADD_PENDO_FN, useValue: jasmine.createSpy() }.
 */
export const ADD_PENDO_FN = new InjectionToken<(config: PendoConfig) => Promise<void>>(
  'ADD_PENDO_FN',
  { factory: () => addPendo }
);

type PendoVisitorPayload = {
  id: string;
  domain?: string;
  role_ehs: string;
  email?: string;
  full_name?: string;
  user_language: string;
  package_version: string;
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const PENDO_INTEGRATION_VERSION = 'v1.0.1';
const INTERNAL_USER_DOMAINS = ['ecoonline.com', 'airsweb.com'];

export function getPendoOrganisationId(organisationId: string): string | null {
  if (organisationId === EMPTY_GUID) {
    return null;
  }

  return organisationId;
}

declare const window: Window & { pendo?: { identify: (data: object) => void } };

@Injectable({
  providedIn: 'root',
})
export class PendoService {
  private pendoInitialised = false;
  private visitorPayload: PendoVisitorPayload | null = null;
  private visitorPayloadPromise: Promise<PendoVisitorPayload> | null = null;

  constructor(
    private readonly msalService: MsalService,
    private readonly tenantService: TenantService,
    @Inject(ADD_PENDO_FN) private readonly addPendoFn: (config: PendoConfig) => Promise<void>
  ) {}

  /** Called once after MSAL auth completes. Loads the Pendo script with visitor data only. */
  initPendoForUser(): void {
    if (this.pendoInitialised) {
      return;
    }

    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return;
    }

    this.pendoInitialised = true;

    const pendoConfig: PendoConfig = {
      product: 'ehs',
      apiKey: environment.pendoApiKey,
      production: environment.production,
      userData: {
        email: account.username,
        fullName: account.name ?? account.username,
        role: 'ehs',
        language: navigator.language,
      },
    };

    void (async () => {
      try {
        this.visitorPayloadPromise = this.buildVisitorPayload(account.username, account.name ?? account.username)
          .then((visitorPayload) => {
            this.visitorPayload = visitorPayload;
            return visitorPayload;
          });
        await this.addPendoFn(pendoConfig);
        // Re-identify if a tenant was selected while the script was loading.
        // Only re-identify when organisationId has already resolved; if it is still
        // null the AppComponent's switchMap subscription is in-flight and will call
        // pendo.identify itself once organisationId emits — re-subscribing here
        // would create a second in-flight listener and cause a double identify.
        const currentTenant = this.tenantService.selectedTenant$.value;
        if (currentTenant && this.tenantService.organisationId.value !== null) {
          this.identifyTenant(currentTenant).subscribe();
        }
      } catch (err) {
        console.error('[PendoService] addPendo failed:', err);
        this.visitorPayload = null;
        this.visitorPayloadPromise = null;
        this.pendoInitialised = false; // allow retry on next auth cycle
      }
    })();
  }

  /**
   * Called on every tenant navigation via switchMap in AppComponent.
   * Waits for the new tenant's organisationId to resolve before identifying.
   * The switchMap ensures a previous in-flight Observable is cancelled automatically
   * if the user navigates to another tenant before this one completes.
   */
  identifyTenant(tenant: Tenant): Observable<void> {
    const envSuffix = environment.production ? '' : '_test';

    return this.tenantService.organisationId.pipe(
      filter((id): id is string => id !== null),
      take(1),
      map((organisationId) => {
        try {
          const identifyPayload = {
            ...(this.visitorPayload ? { visitor: this.visitorPayload } : {}),
            account: {
              id: `${tenant.Value}${envSuffix}`,
              name: tenant.Text,
              ...(tenant.CrmId ? { crm: `ecoehs_${tenant.CrmId}` } : {}),
              organisation_id: getPendoOrganisationId(organisationId),
              is_trial: tenant.ProvisionedAsTrial,
              ...(tenant.TenantStatus ? { tenant_status: tenant.TenantStatus } : {}),
              // These are account-level fields that must travel with the real tenant account,
              // not the dummy 'none_test' account created during initPendoForUser.
              region: 'EU',
              type: 'internal',
            },
          };

          window.pendo?.identify(identifyPayload);
        } catch (err) {
          console.error('[PendoService] identifyTenant failed:', err);
        }
      })
    );
  }

  private async buildVisitorPayload(email: string, fullName: string): Promise<PendoVisitorPayload> {
    const envSuffix = environment.production ? '' : '_test';
    const lowercaseEmail = email.toLowerCase();
    const isInternalUser = INTERNAL_USER_DOMAINS.some((domain) => lowercaseEmail.includes(domain));

    return {
      id: `${await this.sha256(lowercaseEmail)}${envSuffix}`,
      domain: lowercaseEmail.split('@')[1],
      role_ehs: 'ehs',
      email: isInternalUser ? lowercaseEmail : undefined,
      full_name: isInternalUser ? fullName : undefined,
      user_language: navigator.language,
      package_version: PENDO_INTEGRATION_VERSION,
    };
  }

  private async sha256(value: string): Promise<string> {
    if (!crypto?.subtle) {
      return 'insecure_env_user';
    }

    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return [...new Uint8Array(digest)].map((byte) => (`00${byte.toString(16)}`).slice(-2)).join('');
  }
}
